import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useGenerateForms } from 'components/issue/useGenerateForms';
import { areObjectIdsEqual } from 'helpers/idComparison';
import { useRetainedValue } from 'hooks/useRetainedValue';
import type { IssueReferenceName } from 'shared/domain/issue/issueReferences';
import { issueModelToOnSingleView } from 'shared/domain/issue/mapping/toView';
import { transformArrayToHashMap } from 'shared/utils/array';
import { useCompanies } from 'store/company';
import { useContracts } from 'store/contract';
import { useIssueStore } from 'store/issue';
import { useProcesses } from 'store/process';
import { useProject } from 'store/project';
import { useSites } from 'store/site';
import { useCurrentUser } from 'store/user';
import { useUsers } from '../withUsers';
import { IssueOnSingleView } from './IssueOnSingleView';
import { markEventsBeingSaved } from './model';
import { useIssueReferences } from './issueReferences';

/** The issue the view shows, or the reference it cannot be shown without. */
export interface IssueOnSingleViewState {
  /** `undefined` until the issue and the data it names are ready. */
  issue?: IssueOnSingleView;
  missingReference?: IssueReferenceName;
}

/** The issue of the view: the current issue of the store, mapped once the data it names is ready. */
export function useIssueOnSingleView(): IssueOnSingleViewState {
  const intl = useIntl();
  const currentIssueId = useIssueStore.use.currentIssueId();
  const currentIssue = useIssueStore.use.currentIssue();
  const eventsBeingSaved = useIssueStore.use.eventsBeingSaved();
  const { _id: currentUserId } = useCurrentUser();
  const {
    all: { items: users },
    getUserOnViewWithAccessLevelOnIconViewById,
  } = useUsers();
  const { items: sites } = useSites();
  const contractsArray = useContracts().items;
  const { items: companies } = useCompanies();
  const { timezone, organizationId } = useProject();
  const { processes } = useProcesses();
  const { forms, loading: loadingForms } = useGenerateForms();

  const openedIssue =
    currentIssue && areObjectIdsEqual(currentIssue._id, currentIssueId) ? currentIssue : undefined;
  const { references, missing: missingReference } = useIssueReferences(openedIssue);

  const contracts = useMemo(() => transformArrayToHashMap(contractsArray), [contractsArray]);
  const subcontractors = useMemo(() => transformArrayToHashMap(companies), [companies]);

  const mapped = useMemo(() => {
    if (!openedIssue || !references || loadingForms || !users) {
      return undefined;
    }
    const issue = issueModelToOnSingleView({
      issueModel: openedIssue,
      currentUserId,
      references,
      sites,
      processes,
      issueForms: forms,
      subcontractors,
      contractNumbers: contracts,
      timezone,
      organizationId,
      intl,
      getUserOnViewWithAccessLevelOnIconViewById,
    });
    return { ...issue, events: markEventsBeingSaved(issue.events, eventsBeingSaved) };
  }, [
    openedIssue,
    references,
    eventsBeingSaved,
    currentUserId,
    sites,
    processes,
    forms,
    loadingForms,
    users,
    subcontractors,
    contracts,
    timezone,
    organizationId,
    intl,
    getUserOnViewWithAccessLevelOnIconViewById,
  ]);

  // While the data the issue names reloads, the issue already shown stays.
  const shown = useRetainedValue(mapped);
  const issue = shown && currentIssue && areObjectIdsEqual(shown._id, currentIssue._id) ? shown : undefined;
  return useMemo(() => ({ issue, missingReference }), [issue, missingReference]);
}
